import { Copy, FolderOpen, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useFlowNavigation } from '../hooks/useFlowNavigation';
import { useAppStore } from '../store/hooks';
import { FlowStep } from '../store/slices/flowSlice';

interface ProjectManagerCardProps {
  className?: string;
  showCreateInput?: boolean;
  onEmptyStateAction?: () => void;
  /** Maximum number of projects to display. Undefined shows all */
  maxProjects?: number;
  /** Show "View All" link to open the full manager */
  showViewAllLink?: boolean;
}

export function ProjectManagerCard({
  className = '',
  showCreateInput = true,
  onEmptyStateAction,
  maxProjects,
  showViewAllLink = false,
}: ProjectManagerCardProps) {
  const projects = useAppStore((s) => s.projects);
  const currentProjectId = useAppStore((s) => s.currentProjectId);
  const createProject = useAppStore((s) => s.createProject);
  const loadProject = useAppStore((s) => s.loadProject);
  const renameProject = useAppStore((s) => s.renameProject);
  const duplicateProject = useAppStore((s) => s.duplicateProject);
  const deleteProject = useAppStore((s) => s.deleteProject);
  const saveCurrentProject = useAppStore((s) => s.saveCurrentProject);
  const calculateCompletionPercentage = useAppStore((s) => s.calculateCompletionPercentage);

  const { user } = useAuth();
  const { goToNextStep } = useFlowNavigation();
  const [newProjectName, setNewProjectName] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const projectArray = Object.values(projects).sort(
    (a, b) => b.lastModified.getTime() - a.lastModified.getTime()
  );
  const displayProjects =
    typeof maxProjects === 'number' ? projectArray.slice(0, maxProjects) : projectArray;
  const hasProjects = projectArray.length > 0;

  const handleCreate = async () => {
    const name = newProjectName.trim() || 'Untitled Project';
    const id = createProject(name);
    loadProject(id);
    setNewProjectName('');
    goToNextStep();
  };

  const handleOpen = async (id: string) => {
    // Save current project first
    if (currentProjectId) {
      saveCurrentProject();
    }
    loadProject(id);
    const loaded = useAppStore.getState().projects[id];
    if (loaded && loaded.flowState.currentStep === FlowStep.LANDING) {
      goToNextStep();
    }
  };

  const handleRename = (id: string) => {
    const project = projects[id];
    if (!project) return;
    const name = prompt('Rename project', project.name);
    if (name) {
      renameProject(id, name);
    }
  };

  const handleFork = (id: string) => {
    const project = projects[id];
    if (!project) return;
    const name = prompt('Name for forked project', `${project.name} Fork`);
    if (name) {
      const newId = duplicateProject(id, name);
      loadProject(newId);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Delete this project?')) {
      deleteProject(id);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    // Local projects are already up to date
    setTimeout(() => setIsRefreshing(false), 300);
  };

  return (
    <div
      className={`bg-[#0c1222]/80 backdrop-blur border border-[#1a2d4d] rounded-xl p-6 shadow-lg flex flex-col h-full ${className}`}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-1 h-6 bg-gradient-to-b from-[#4f46e5] to-[#06b6d4] rounded-full mr-3"></div>
          <h3 className="text-xl font-semibold text-white">Your Projects</h3>
          {showViewAllLink && (
            <button
              onClick={() => useAppStore.setState({ currentProjectId: null })}
              className="ml-3 text-sm text-gray-400 hover:text-white transition-colors underline"
            >
              View All
            </button>
          )}
        </div>
        {user && (
          <div className="flex items-center gap-2">
            {hasProjects && onEmptyStateAction && !showCreateInput && (
              <button
                onClick={onEmptyStateAction}
                className="text-gray-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-[#1a2d4d]/50"
                title="Start New Project"
              >
                <Plus className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="text-gray-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-[#1a2d4d]/50"
              title="Refresh from cloud"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        )}
      </div>

      {showCreateInput && (
        <div className="flex gap-3 mb-6">
          <input
            className="flex-1 bg-[#050614] border border-[#1a2d4d] rounded-lg p-3 text-white text-sm focus:border-[#4f46e5] focus:outline-none transition-colors"
            placeholder="New project name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
          <button
            onClick={handleCreate}
            className="bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] hover:from-[#4338ca] hover:to-[#0891b2] text-white px-4 py-3 rounded-lg flex items-center gap-2 text-sm font-medium transition-all duration-200 hover:scale-105"
          >
            <Plus className="w-4 h-4" /> Create
          </button>
        </div>
      )}

      <div className="space-y-4 overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-[#1a2d4d] scrollbar-track-transparent">
        {projectArray.length === 0 ? (
          <div className="space-y-6">
            <div className="text-center text-gray-400 py-8">
              <button
                onClick={onEmptyStateAction}
                className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#050614] border border-[#1a2d4d] flex items-center justify-center hover:bg-[#1a2d4d]/20 transition-colors"
              >
                <Plus className="w-8 h-8 text-gray-500" />
              </button>
              <div className="text-base font-medium mb-1">No projects yet</div>
              <div className="text-sm">Ready to create your first interactive story?</div>
            </div>
          </div>
        ) : (
          displayProjects.map((p) => (
            <div
              key={p.id}
              className="bg-[#050614] border border-[#1a2d4d] rounded-xl p-4 flex flex-col gap-3 hover:border-[#4f46e5]/30 transition-all duration-200 hover:bg-[#050614]/80"
            >
              <div className="flex justify-between items-start">
                <div className="font-semibold text-white text-base truncate mr-2 flex items-center gap-2">
                  {p.name}
                  {p.isLocalOnly && (
                    <span className="text-xs text-red-500 bg-red-500/20 px-2 py-0.5 rounded-full font-medium">
                      LOCAL-ONLY
                    </span>
                  )}
                </div>
                {currentProjectId === p.id && (
                  <span className="text-xs text-[#4f46e5] bg-[#4f46e5]/20 px-2 py-1 rounded-full font-medium flex-shrink-0">
                    Current
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span>{p.lastModified.toLocaleDateString()}</span>
                <span>{calculateCompletionPercentage(p)}% complete</span>
              </div>
              <div className="h-2 bg-gray-700/50 rounded-full overflow-hidden">
                <div
                  className="h-2 bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] rounded-full transition-all duration-300"
                  style={{ width: `${calculateCompletionPercentage(p)}%` }}
                ></div>
              </div>
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => handleOpen(p.id)}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-all duration-200 hover:scale-[1.02]"
                >
                  <FolderOpen className="w-4 h-4" /> Open
                </button>
                <button
                  onClick={() => handleRename(p.id)}
                  className="bg-gray-600/80 hover:bg-gray-600 text-white px-3 py-2 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
                  title="Rename"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleFork(p.id)}
                  className="bg-gray-600/80 hover:bg-gray-600 text-white px-3 py-2 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
                  title="Fork into New Save"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="bg-red-600/80 hover:bg-red-600 text-white px-3 py-2 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
